import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { deleteTrackFromStudyPlan, getTrackSectionsById, rescheduleUserTrack } from '@/store/tracks/actions';

import { selectTracks } from '@/store/tracks/selectors';

import { message, Modal, Tag } from 'antd';
import CalendarModal from '@/pages/CalendarModal/index.jsx';
import { FINISHED } from '@/pages/TrackId/constants';
import { DropdownMenu } from '@/components';
import TrackProgress from '@/components/TrackProgress';

import useImage from '@shared/hooks/useImage';
import useIsMobile from '@shared/hooks/useIsMobile';

import Utils from '@/Utils';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

import css from './TrackCard.module.scss';

const TrackCard = ({ track, reloadTracks }) => {
  const { t } = useTranslation('TrackCard');
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Получение картинки
  const cover = useImage(track?.coverImage, 'track');
  const [statPer, setStatPer] = useState(0);
  const [modal, modalHolder] = Modal.useModal();
  const [messageApi, contextMessageApi] = message.useMessage();
  const [isMobile] = useIsMobile(769);
  const [isCalendarModalVisible, setIsCalendarModalVisible] = useState(false);
  const { trackSections } = useSelector(selectTracks);

  const showCalendarModal = () => setIsCalendarModalVisible(true);

  const onTrackClick = () => navigate(`/track/${track?.trackId}`);

  const handleClick = isMobile ? onTrackClick : undefined;

  const minTrackDate = trackSections && Utils.getMinimumAllowedDate(trackSections);

  const confirmDeleteTrack = () => {
    modal.confirm({
      title: t('titleDeleteTrack'),
      content: t('confirmDeleteTrack'),
      icon: null,
      okText: t('delete'),
      cancelText: t('cancel'),
      centered: true,
      ...(isMobile && {
        footer: (_, { OkBtn, CancelBtn }) => (
          <div className={css['Footer']}>
            <OkBtn />
            <CancelBtn />
          </div>
        ),
      }),
      onOk: () => {
        dispatch(deleteTrackFromStudyPlan(track?.trackId, reloadTracks, () => messageApi.error(t('deleteError'))));
      },
    });
  };

  const handleRescheduleTrackRequest = () => dispatch(getTrackSectionsById(track?.trackId, showCalendarModal));

  const onAction = actionKey => {
    switch (actionKey) {
      case 'deleteTrack':
        confirmDeleteTrack();
        break;
      case 'rescheduleTrack':
        handleRescheduleTrackRequest();
        break;
      default:
        break;
    }
  };

  const onClickActionsMenu = ({ key }) => onAction(key);

  const additionalActions = [
    { key: 'rescheduleTrack', label: t('rescheduleTrack') },
    { key: 'deleteTrack', label: t('deleteTrack') },
  ];

  const isTrackNecessary = track?.addedByAdmin;

  const handleRescheduleTrack = (_, selectedMonths) => {
    const startDate = Array.isArray(selectedMonths) && selectedMonths.length > 0 ? selectedMonths[0] : null;

    if (startDate) {
      dispatch(
        rescheduleUserTrack(
          { trackId: Number(track?.trackId), startDate },
          () => {
            reloadTracks();
            setIsCalendarModalVisible(false);
          },
          () => messageApi.error(t('rescheduleError'))
        )
      );
    }
  };

  // Считаем статистику
  useEffect(() => {
    if (track?.finishedTopics && track?.totalTopics) {
      setStatPer(Math.round((track.finishedTopics / track.totalTopics) * 100));
    }
  }, []);

  return (
    <div className={css['TrackCard-container']} data-qa='trackCard'>
      <div className={css['TrackCard-container-box']} onClick={!isMobile ? onTrackClick : undefined}>
        <img className={css['TrackCard-cover']} src={cover} alt='TrackCardCover' onClick={handleClick} />
        {isMobile &&
          (isTrackNecessary ? (
            <Tag color='error'>{t('necessaryTrack')}</Tag>
          ) : (
            <Tag color='processing'>{t('optionalTrack')}</Tag>
          ))}
        <div className={css['TrackCard-container-text']}>
          <div className={css['TrackCard-titleRow']}>
            <p className={css['TrackCard-title']}>{track?.trackName}</p>
          </div>
          <p onClick={handleClick}>{track?.shortDescription}</p>
          {isMobile && (
            <div className={css['TrackCard-mobTitle']}>
              <p className={css['TrackCard-mobTitle-status']} onClick={handleClick}>
                {track?.statusName}
              </p>
              {!isTrackNecessary && track?.status !== FINISHED && (
                <DropdownMenu menu={additionalActions} onClick={onClickActionsMenu} />
              )}
            </div>
          )}
          <p onClick={handleClick}>{t('deadline', { deadline: dayjs(track?.startDate).format('LL') })}</p>
        </div>
      </div>

      <div className={css['TrackCard-stat']}>
        <div className={css['TrackCard-stat-title']}>
          <p>
            {!isMobile &&
              (isTrackNecessary ? (
                <Tag color='error'>{t('necessaryTrack')}</Tag>
              ) : (
                <Tag color='processing'>{t('optionalTrack')}</Tag>
              ))}
          </p>

          {!isMobile && (
            <div className={css['TrackCard-stat-status']} onClick={handleClick}>
              {track?.statusName}
            </div>
          )}

          {!isTrackNecessary && !isMobile && track?.status !== FINISHED && (
            <div className={css['TrackCard-actions']}>
              <DropdownMenu menu={additionalActions} onClick={onClickActionsMenu} />
            </div>
          )}
        </div>
        <TrackProgress statPer={statPer} finishedTopics={track?.finishedTopics} totalTopics={track?.totalTopics} />
      </div>

      {isCalendarModalVisible && (
        <CalendarModal
          open={isCalendarModalVisible}
          onOk={handleRescheduleTrack}
          title={t('newTrackDeadline')}
          onCancel={() => setIsCalendarModalVisible(false)}
          okText={t('rescheduleTrack')}
          selectMonthEvents
          minTrackDate={minTrackDate}
        />
      )}

      {modalHolder}
      {contextMessageApi}
    </div>
  );
};

TrackCard.propTypes = { track: PropTypes.object, reloadTracks: PropTypes.func };

export default TrackCard;
